import React, { useMemo, useState } from "react";
import * as UTable from "ubs-framework/v2/common/UTable";
import moment from "moment";
import UAvatar from "ubs-framework/v2/UAvatar";
import SideModal from "../side-modal";
import {FilterSelect} from "ubs-framework/v2/common/UFilters/FilterSelect/filter-select";
import UTags from "ubs-framework/v2/UTags";
import {COLOR} from "ubs-framework/v2/common/constant";
/* ==================  UAssignHistory  ==================
 * Props:
 *   open          – boolean
 *   close         – () => void
 *   socketData    – object  (shape: { totalRecords, data: [...] })
 *   loading       – boolean
 *   onFilter      – (userId) => void   (called when user-filter changes)
 *   onPagination  – (payload) => void  (called on page / limit change)
 * ======================================================= */

const PAGINATION = { LIMIT: 20, CURRENT_PAGE: 1 };

const UAssignHistory = ({ open, close, socketData, loading, onFilter, onPagination }) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [currentPage, setCurrentPage] = useState(PAGINATION.CURRENT_PAGE);
    const [limit, setLimit] = useState(PAGINATION.LIMIT);

    /* Build unique user options for the filter dropdown */
    const userOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        (socketData?.data || []).forEach((row) => {
            const d = row?.employeeDetails;
            const id = row?.user;
            if (d && id && !seen.has(id)) {
                seen.add(id);
                options.push({
                    label: `${d.firstName || ""} ${d.lastName || ""}`.trim(),
                    value: id,
                });
            }
        });
        return options;
    }, [socketData]);

    /* Table columns */
    const header = useMemo(() => [
        {
            title: "Date & Time",
            param: "createdAt",
            render: (data) =>
                data?.createdAt
                    ? moment(data?.createdAt).format("DD/MM/YYYY hh:mm A")
                    : "-",
        },
        {
            title: "Employee",
            render: (data) => {
                const d = data?.employeeDetails;
                const name = d ? `${d.firstName || ""} ${d.lastName || ""}`.trim() : "-";
                return (
                    <div className="u_flex u_align-center u_gap-10">
                        <UAvatar name={name} image={d?.profileImage || ""} width={30} />
                        <div className={"u_ml-5"}>
                            <p className="u_one-line u_small-font">{name || "-"}</p>
                            {(d?.department || d?.employeeId) && (
                                <span className="u_font u_color-subtitle u_one-line">
                                    {d?.department} - {d?.employeeId}
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Action",
            param: "action",
            render: (data) =><span className="u_small-font u_color-subtitle">{data?.action || "-"}</span>

        },
        {
            title: "Action By",
            render: (data) => {
                const d = data?.actionByDetails;
                const name = d ? `${d.firstName || ""} ${d.lastName || ""}`.trim() : "-";
                return (
                    <div className="u_flex u_align-center u_gap-10">
                        <UAvatar name={name} image={d?.profileImage || ""}   width={30}/>
                        <div className={"u_ml-5"}>
                            <p className="u_one-line u_small-font">{name || "-"}</p>
                            {(d?.department || d?.employeeId) && (
                                <span className="u_font u_color-subtitle u_one-line">
                                    {d?.department} - {d?.employeeId}
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "IP Address",
            param: "ipAddress",
            render: (data) => (
                <span className="u_small-font u_color-subtitle">{data?.ipAddress || "-"}</span>
            ),
        },
    ], []);

    /* Pagination handler */
    const handlePagination = (payload) => {
        setCurrentPage(payload?.currentPage ?? currentPage);
        setLimit(payload?.limit ?? limit);
        onPagination && onPagination(payload);
    };

    /* User filter handler */
    const handleUserFilter = (option) => {
        setSelectedUser(option);
        setCurrentPage(1);
        onFilter && onFilter(option?.value || null);
    };

    return (
        <SideModal
            open={open}
            onClose={close}
            name="ESS Type History"
            header={<h4 className="u_text-c">Activity Logs</h4>}
            footer={<></>}
            maxWidth={1100}
        >
            <div className="u_activity-log-details">
                {/* Header bar */}
                <div className="u_activity-log-details-header u_flex u_flex-align u_items-center u_content-end u_px-20 u_py-10">
                    <div>
                        <FilterSelect
                            type="user"
                            params={{
                                options: userOptions,
                                value: selectedUser,
                                onChange: handleUserFilter,
                                isClearable: true,
                                placeholder: "All Users",
                            }}
                        />
                    </div>
                </div>

                {/* Table */}
                <UTable.Table
                    header={header}
                    data={socketData?.data || []}
                    totalRecords={socketData?.totalRecords || 0}
                    onPagination={handlePagination}
                    currentPage={currentPage}
                    limit={limit}
                    dataLoading={loading}
                    skeletonColumn={5}
                    className="u_table-resource u_table-resource-nowrap u_table-resource-no-sticky"
                />
            </div>
        </SideModal>
    );
};

export default UAssignHistory;
